import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/apiClient';

export interface AppNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  actionUrl?: string;
  actionLabel?: string;
  priority: string;
  relatedEntityType?: string;
  relatedEntityId?: number;
  isRead: boolean;
  readAt?: string;
  isDismissed: boolean;
  createdAt: string;
}

const QUERY_KEY = ['notifications'] as const;

export function useNotifications() {
  return useQuery<AppNotification[]>({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<AppNotification[]>('/notifications').then(r => r ?? []),
    staleTime: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/notifications/${id}/read`, {}),
    onMutate: async (id: number) => {
      await qc.cancelQueries({ queryKey: QUERY_KEY });
      const previous = qc.getQueryData<AppNotification[]>(QUERY_KEY);
      qc.setQueryData<AppNotification[]>(QUERY_KEY, old =>
        old?.map(n => n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n) ?? []
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) qc.setQueryData(QUERY_KEY, context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/notifications/read-all', {}),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: QUERY_KEY });
      const previous = qc.getQueryData<AppNotification[]>(QUERY_KEY);
      qc.setQueryData<AppNotification[]>(QUERY_KEY, old =>
        old?.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })) ?? []
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(QUERY_KEY, context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/notifications/${id}/dismiss`, {}),
    onMutate: async (id: number) => {
      await qc.cancelQueries({ queryKey: QUERY_KEY });
      const previous = qc.getQueryData<AppNotification[]>(QUERY_KEY);
      qc.setQueryData<AppNotification[]>(QUERY_KEY, old =>
        old?.filter(n => n.id !== id) ?? []
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) qc.setQueryData(QUERY_KEY, context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
